/* global AbortSignal */
/** Клиент раздела «Аналитика ОПТ» (`/api/wholesale/*`, backend `wholesale/`, право
 *  `wholesale_analytics`) по витрине dwh `mart.wholesale_*` (контракт `docs/contract/wholesale.md` v1.4).
 *
 *  Портал не переоценивает строки: агрегаты — Σ колонок витрины. null ≠ 0: деньги null — нет ни одной
 *  строки, где они есть (нет цены / нет суммы заказа); агрегат вида null — строк этого вида нет. */
import { api } from "./client";

const BASE = "/wholesale";

export type Kind = "shipped" | "planned" | "overdue";
export const KINDS: readonly Kind[] = ["shipped", "planned", "overdue"];
export type PriceStatus = "ok" | "no_price" | "no_warehouse_rate";
/** `zero_amount` (v1.4) — отгрузка со штуками и суммой 0: выручка 0 — факт (не «нет цены»), строка
 *  оценена (маржа — только расход). Остальные ≠ ok — маржи нет. */
export type RevenueStatus = "ok" | "zero_amount" | "zero_price" | "no_order_line" | "unknown_unit";
export type MonthBasis = "shipment" | "base_shipment" | "base_unresolved" | "order_shipment_date";

/** Строки / штуки / выручка с НДС подмножества строк (что не вошло, статус).
 *  `revenue_gross` — Σ только по строкам, где сумма известна; null — в бакете есть строки, но ни
 *  у одной нет суммы («сумма неизвестна», не 0 ₽); `revenue_unknown_rows` — сколько строк бакета
 *  без суммы (no_order_line / unknown_unit) — они в сумму не входят. */
export interface Bucket {
  rows: number;
  quantity: number;
  revenue_gross: number | null;
  revenue_unknown_rows: number;
}

/** Причина «не вошло» из `wholesale_state` (штуки и выручка — как в jsonb, могут быть null). */
export interface ExcludedReason {
  reason: string;
  /** причина есть в контракте; false — новая, экран показывает её как есть */
  known: boolean;
  rows: number;
  quantity: number | null;
  revenue_gross: number | null;
  /** число разных заказов — только у причин остатков заказов (контракт v1.3); null — сборка до v1.3 */
  orders?: number | null;
}

export interface WholesaleState {
  built_at: string;
  /** сборка dbt (контракт v1.2); у данных ≠ у state — бэкенд отвечает 503 «пересобирается или собрана частично» */
  build_id: string;
  orders_as_of: string;
  sales_from: string | null;
  organization_ref: string | null;
  sales_loaded_at: string;
  sales_date_to: string | null;
  orders_snapshot_at: string | null;
  shipped_excluded: ExcludedReason[];
  orders_excluded: ExcludedReason[];
  shipped_month_unresolved: { rows: number; quantity: number | null; revenue_gross: number | null };
}

/** Агрегат строк витрины одного вида. Деньги — Σ по строкам, где они есть (null — ни одной). */
export interface Agg {
  rows: number;
  quantity: number;
  revenue_gross: number | null;
  revenue_vat: number | null;
  revenue_net: number | null;
  /** строки без выручки (no_order_line / unknown_unit) */
  revenue_missing: Bucket;
  /** себестоимость производства, ₽ С НДС, знак витрины (расход −) */
  cogs_goods: number | null;
  cogs_goods_missing: Bucket;
  /** склад производства, ₽ БЕЗ НДС, знак витрины */
  cogs_wh: number | null;
  cogs_wh_missing: Bucket;
  /** маржа по оценённым строкам (price_status = ok и revenue_status ok / zero_amount) */
  net_margin: number | null;
  /** выручка оценённых строк — база маржинальности */
  margin_base_revenue: number;
  margin_pct: number | null;
  /** строки без маржи: нет цены / нет суммы заказа */
  unvalued: Bucket;
  /** все строки оценены */
  complete: boolean;
  price_status: Record<Exclude<PriceStatus, "ok">, Bucket>;
  /** бакет по каждому статусу ≠ ok (в т.ч. `zero_amount` — оценённые строки за 0 ₽) */
  revenue_status: Record<Exclude<RevenueStatus, "ok">, Bucket>;
  no_inn: Bucket;
  base_unresolved: Bucket;
  /** строки комиссионера (v1.4: `method` передачи на комиссию / возврата от комиссионера); rows > 0 —
   *  покупатель — комиссионер: выручка по передаче на комиссию, по цене передачи */
  commission: Bucket;
}

export type PerKind = Record<Kind, Agg | null>;

export interface Envelope {
  state: WholesaleState;
  from_cache: boolean;
}

export interface WholesaleMeta extends Envelope {
  month_min: string | null;
  month_max: string | null;
  kinds: Record<Kind, { month_min: string; month_max: string; rows: number } | null>;
}

export interface MonthRow extends PerKind {
  month: string;
}

export interface OpenOrdersAgg extends Agg {
  month_min: string;
  month_max: string;
}

/** «К отгрузке» с датой отгрузки вне периода — справка под KPI, ни во что не входит; `months` —
 *  месяцы отгрузки по порядку. */
export interface OutsideAgg extends Agg {
  months: string[];
}

/** Период сравнения и отгрузки за него (Δ KPI); `shipped` null — отгрузок в нём нет. */
export interface CmpShipped {
  month_from: string;
  month_to: string;
  shipped: Agg | null;
}

export interface WholesaleSummary extends Envelope {
  month_from: string;
  month_to: string;
  months: MonthRow[];
  totals: PerKind;
  /** портфель заказов (решение владельца 23.10.2026; маржа — плановая): `planned` — с датой
   *  отгрузки в периоде, `overdue` — все просроченные на дату снимка (без периода);
   *  `planned_after` / `planned_before` — к отгрузке позже конца / раньше начала периода (справка) */
  open_orders: {
    planned: OpenOrdersAgg | null;
    overdue: OpenOrdersAgg | null;
    planned_after: OutsideAgg | null;
    planned_before: OutsideAgg | null;
  };
  /** сравнение (запрошено `cmp_from` / `cmp_to`), иначе null */
  cmp: CmpShipped | null;
}

export interface ProductRow extends PerKind {
  nomenclature_ref: string;
  characteristic_ref: string | null;
  code_1c: string | null;
  article_1c: string | null;
  name_1c: string | null;
  characteristic_name: string | null;
  price_min: number | null;
  price_max: number | null;
  date_min: string | null;
  date_max: string | null;
}

export interface WholesaleProducts extends Envelope {
  month_from: string;
  month_to: string;
  unpriced: boolean;
  no_rate: boolean;
  /** область экрана: отгрузки периода + все открытые заказы */
  portfolio: boolean;
  products: ProductRow[];
}

export interface Line {
  row_id: string;
  kind: Kind;
  month: string;
  month_basis: MonthBasis;
  date: string;
  document_type: string;
  document_ref: string;
  document_line: number | null;
  base_document_ref: string | null;
  base_date: string | null;
  order_ref: string | null;
  order_number: string | null;
  counterparty_ref: string;
  buyer_name: string | null;
  buyer_inn: string | null;
  buyer_inn_status: "ok" | "no_inn";
  nomenclature_ref: string;
  code_1c: string | null;
  article_1c: string | null;
  name_1c: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  warehouse_ref: string | null;
  quantity: number;
  revenue_status: RevenueStatus;
  revenue_gross: number | null;
  revenue_vat: number | null;
  revenue_net: number | null;
  price_status: PriceStatus;
  purchase_price_id: number | null;
  purchase_price_gross: number | null;
  cogs_goods: number | null;
  warehouse_rate: number | null;
  cogs_wh: number | null;
  net_margin: number | null;
  method: string;
}

/** Фильтр drill-down: `characteristic: ""` — строки без характеристики. */
export interface LinesQuery {
  month_from: string;
  month_to: string;
  kind?: Kind;
  buyer?: string;
  nomenclature?: string;
  characteristic?: string;
  /** имя характеристики (узел дерева без товара выше); "" — без характеристики */
  characteristic_name?: string;
  /** order_ref; "" — отгрузки без заказа */
  order?: string;
  /** только строки без закупочной цены (`no_price`) */
  unpriced?: boolean;
  /** только строки без ставки склада (`no_warehouse_rate`); с `unpriced` вместе нельзя */
  no_rate?: boolean;
  /** отгрузки периода + ВСЕ открытые заказы (портфель, без периода) — карточки, «без цены» */
  portfolio?: boolean;
  /** один месяц у всех видов, YYYY-MM (шаг «Месяц» пути дерева) */
  month?: string;
}

export interface WholesaleLines extends Envelope {
  month_from: string;
  month_to: string;
  totals: PerKind;
  total: number;
  limit: number;
  truncated: boolean;
  lines: Line[];
  /** эхо фильтра */
  filters: {
    kind: Kind | null;
    buyer: string | null;
    nomenclature: string | null;
    characteristic: string | null;
    characteristic_name: string | null;
    order: string | null;
    unpriced: boolean;
    no_rate: boolean;
    portfolio: boolean;
    month: string | null;
  };
}

export async function getWholesaleMeta(signal?: AbortSignal): Promise<WholesaleMeta> {
  return (await api.get<WholesaleMeta>(`${BASE}/meta`, { signal })).data;
}

/** Месяцы периода сравнения (Δ). */
export interface MonthSpan {
  from: string;
  to: string;
}

export async function getWholesaleSummary(
  month_from: string,
  month_to: string,
  cmp: MonthSpan | null,
  signal?: AbortSignal,
): Promise<WholesaleSummary> {
  const params: Record<string, string> = { month_from, month_to };
  if (cmp) {
    params.cmp_from = cmp.from;
    params.cmp_to = cmp.to;
  }
  return (await api.get<WholesaleSummary>(`${BASE}/summary`, { params, signal })).data;
}

/** Список товаров: все / только без закупочной цены / только без ставки склада. */
export type ProductsFilter = "all" | "unpriced" | "no_rate";

/** `portfolio` — область экрана (отгрузки периода + все открытые заказы), как у плашки покрытия. */
export async function getWholesaleProducts(
  month_from: string,
  month_to: string,
  filter: ProductsFilter,
  portfolio: boolean,
  signal?: AbortSignal,
): Promise<WholesaleProducts> {
  const params = {
    month_from,
    month_to,
    ...(filter === "unpriced" ? { unpriced: true } : {}),
    ...(filter === "no_rate" ? { no_rate: true } : {}),
    ...(portfolio ? { portfolio: true } : {}),
  };
  return (await api.get<WholesaleProducts>(`${BASE}/products`, { params, signal })).data;
}

export async function getWholesaleLines(q: LinesQuery, signal?: AbortSignal): Promise<WholesaleLines> {
  const params: Record<string, string | boolean> = { month_from: q.month_from, month_to: q.month_to };
  if (q.kind) params.kind = q.kind;
  if (q.buyer !== undefined) params.buyer = q.buyer;
  if (q.nomenclature !== undefined) params.nomenclature = q.nomenclature;
  if (q.characteristic !== undefined) params.characteristic = q.characteristic;
  if (q.characteristic_name !== undefined) params.characteristic_name = q.characteristic_name;
  if (q.order !== undefined) params.order = q.order;
  if (q.unpriced) params.unpriced = true;
  if (q.no_rate) params.no_rate = true;
  if (q.portfolio) params.portfolio = true;
  if (q.month !== undefined) params.month = q.month;
  return (await api.get<WholesaleLines>(`${BASE}/lines`, { params, signal })).data;
}

// ─────────────── дерево (`GET /tree`) ───────────────
/** Измерения дерева — белый список бэкенда (`wholesale/tree.py` `DIMS`); склада нет. */
export type TreeDim = "buyer" | "product" | "characteristic" | "month" | "order";
/** Сортировка уровня (на бэкенде, по всему уровню): пустое значение — в конце. */
export type TreeSortKey = "revenue" | "quantity" | "margin" | "margin_pct" | "name" | "planned" | "overdue";
export type TreeSortDir = "desc" | "asc";

/** Узел уровня. `key` — ключ для пути (null — «без заказа» / «без характеристики»; месяц — YYYY-MM).
 *  Агрегаты по видам (как в сводке, null ≠ 0; null агрегата — строк этого вида у узла нет):
 *  `shipped` — отгрузки периода, `planned` / `overdue` — портфель открытых заказов на дату снимка
 *  (ВСЕ месяцы, не период; маржа — плановая), `shipped_cmp` — отгрузки периода сравнения (Δ). */
export interface TreeNode {
  key: string | null;
  label: string;
  sublabel: string | null;
  has_children: boolean;
  shipped: Agg | null;
  planned: Agg | null;
  overdue: Agg | null;
  shipped_cmp: Agg | null;
}

export interface TreeQuery {
  month_from: string;
  month_to: string;
  levels: TreeDim[];
  /** ключи раскрытых уровней по порядку `levels`; ответ — уровень `levels[path.length]` */
  path: (string | null)[];
  sort: TreeSortKey;
  dir: TreeSortDir;
  limit: number;
  offset: number;
  /** период сравнения (Δ); на уровнях с месяцем не передаётся (бэкенд — 422) */
  cmp: MonthSpan | null;
}

export interface WholesaleTree extends Envelope {
  month_from: string;
  month_to: string;
  dim: TreeDim;
  levels: TreeDim[];
  path: (string | null)[];
  cmp: { month_from: string; month_to: string } | null;
  sort: TreeSortKey;
  dir: TreeSortDir;
  limit: number;
  offset: number;
  /** узлов на уровне всего (страница — `nodes`) */
  total_count: number;
  nodes: TreeNode[];
  /** сборка, по которой посчитан уровень: страницы с разным signal не склеиваются */
  signal: string;
}

export async function getWholesaleTree(q: TreeQuery, signal?: AbortSignal): Promise<WholesaleTree> {
  const params: Record<string, string | number> = {
    month_from: q.month_from,
    month_to: q.month_to,
    levels: q.levels.join(","),
    sort: q.sort,
    dir: q.dir,
    limit: q.limit,
    offset: q.offset,
  };
  if (q.path.length > 0) params.path = JSON.stringify(q.path);
  if (q.cmp) {
    params.cmp_from = q.cmp.from;
    params.cmp_to = q.cmp.to;
  }
  return (await api.get<WholesaleTree>(`${BASE}/tree`, { params, signal })).data;
}

/** Один узел (карточка покупателя / товара / заказа / узла): те же агрегаты, что у строки дерева;
 *  `node` null — ни отгрузок периода, ни открытых заказов, ни отгрузок сравнения. */
export interface WholesaleNode extends Envelope {
  /** измерение узла (последний уровень пути) */
  dim: TreeDim;
  month_from: string;
  month_to: string;
  cmp: { month_from: string; month_to: string } | null;
  levels: TreeDim[];
  path: (string | null)[];
  signal: string;
  node: TreeNode | null;
}

export interface NodeQuery {
  month_from: string;
  month_to: string;
  levels: TreeDim[];
  /** ключи ВСЕХ уровней — путь до узла */
  path: (string | null)[];
  cmp: MonthSpan | null;
}

export async function getWholesaleNode(q: NodeQuery, signal?: AbortSignal): Promise<WholesaleNode> {
  const params: Record<string, string> = {
    month_from: q.month_from,
    month_to: q.month_to,
    levels: q.levels.join(","),
    path: JSON.stringify(q.path),
  };
  if (q.cmp) {
    params.cmp_from = q.cmp.from;
    params.cmp_to = q.cmp.to;
  }
  return (await api.get<WholesaleNode>(`${BASE}/node`, { params, signal })).data;
}
